import React, { useState } from 'react';
import Modal from './Modal';
import { EyeIcon, EyeOffIcon } from '../utils/icons';
import { usersApi } from '../services/api';
import logger from '../utils/logger';
import './ChangePasswordModal.css';

export default function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.new_password.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    setSaving(true);
    try {
      await usersApi.changePassword({
        current_password: form.current_password,
        new_password: form.new_password,
      });
      logger.info('auth', 'Password changed successfully');
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <Modal title="Change Password" size="sm" onClose={onClose}>
        <div className="modal-body">
          <div className="cp-success">
            <SuccessIcon />
            <p>Password changed successfully</p>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Change Password" size="sm" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && <div className="cp-error">{error}</div>}

          <div className="form-group">
            <label className="form-label" htmlFor="cp-current">Current Password</label>
            <div className="cp-password-wrap">
              <input
                id="cp-current"
                className="input"
                type={showCurrent ? 'text' : 'password'}
                name="current_password"
                value={form.current_password}
                onChange={handleChange}
                required
                autoFocus
              />
              <button
                type="button"
                className="cp-toggle-vis"
                onClick={() => setShowCurrent((v) => !v)}
                tabIndex={-1}
              >
                {showCurrent ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="cp-new">New Password</label>
            <div className="cp-password-wrap">
              <input
                id="cp-new"
                className="input"
                type={showNew ? 'text' : 'password'}
                name="new_password"
                value={form.new_password}
                onChange={handleChange}
                required
                minLength={6}
              />
              <button
                type="button"
                className="cp-toggle-vis"
                onClick={() => setShowNew((v) => !v)}
                tabIndex={-1}
              >
                {showNew ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="cp-confirm">Confirm New Password</label>
            <div className="cp-password-wrap">
              <input
                id="cp-confirm"
                className="input"
                type={showNew ? 'text' : 'password'}
                name="confirm_password"
                value={form.confirm_password}
                onChange={handleChange}
                required
                minLength={6}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Change Password'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SuccessIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" stroke="var(--accent)" strokeWidth="2" />
      <path d="M10 16.5l4 4 8-8" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
